import React from 'react';
import type {ReactElement} from 'react';
import {IMaskInput} from 'react-imask';

import {useIMaskInput} from '../../hooks/useIMaskInput';

/**
 * Custom definitions for IMask patterns
 * Used for alphanumeric masks like CNPJ
 */
export interface IMaskDefinitions {
  [key: string]: RegExp | string;
}

/**
 * Mask type - can be string, RegExp, or array
 */
type MaskType = string | RegExp | Array<string | RegExp>;

/**
 * Props for IMaskInputWrapper component
 * Extends standard HTML input attributes
 */
export interface IMaskInputWrapperProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'mask'
> {
  /** Mask pattern - can be string, RegExp, or array */
  mask: MaskType;
  /** Current input value */
  value: string;
  /** Change handler that receives formatted value */
  onChange?: (event: {target: {value: string}}) => void;
  /** Custom character definitions for mask patterns */
  definitions?: IMaskDefinitions;
  /** Placeholder character (default: '_') */
  placeholderChar?: string;
  /** Whether to show placeholder immediately (default: false) */
  lazy?: boolean;
  /** Whether to return unmasked value (default: false) */
  unmask?: boolean;
}

/**
 * Converts react-text-mask format to react-imask format
 * react-text-mask used '*' for any character
 * react-imask uses '0' for digits, 'a' for letters, '*' for any
 *
 * @param mask - Mask in react-text-mask format
 * @returns Mask in react-imask format
 *
 * @example
 * convertMaskFormat('***************') // Returns '000000000000000'
 * convertMaskFormat('(99) 99999-9999') // Returns '(99) 99999-9999' (unchanged)
 */
const convertMaskFormat = (mask: MaskType): MaskType => {
  if (typeof mask === 'string') {
    // Convert asterisks to zeros for numeric masks (bank accounts)
    // This maintains compatibility with react-text-mask behavior
    return mask.replace(/\*/g, '0');
  }
  return mask;
};

/**
 * Wrapper for IMaskInput that replicates react-text-mask behavior
 *
 * Features:
 * - Controlled component (accepts value prop)
 * - Returns masked value in onChange
 * - Supports custom definitions
 * - Automatically converts asterisk masks to zero masks
 * - Full TypeScript support
 *
 * @example
 * ```tsx
 * <IMaskInputWrapper
 *   mask="00.000.000/0000-00"
 *   value={cnpj}
 *   onChange={(e) => setCnpj(e.target.value)}
 *   placeholder="Digite o CNPJ"
 * />
 * ```
 *
 * @example With custom definitions
 * ```tsx
 * <IMaskInputWrapper
 *   mask="AA.000.000/0000-00"
 *   definitions={{A: /[A-Z0-9]/}}
 *   value={code}
 *   onChange={(e) => setCode(e.target.value)}
 * />
 * ```
 */
export const IMaskInputWrapper = ({
  onChange,
  value,
  mask,
  definitions,
  placeholderChar = '_',
  lazy = false,
  unmask = false,
  ...props
}: IMaskInputWrapperProps): ReactElement => {
  const {handleAccept} = useIMaskInput(onChange);
  const convertedMask = convertMaskFormat(mask);

  return (
    <IMaskInput
      {...props}
      value={value}
      mask={convertedMask as any}
      definitions={definitions}
      onAccept={handleAccept}
      unmask={unmask}
      lazy={lazy}
      placeholderChar={placeholderChar}
    />
  );
};
